import { logger } from '@adornis/base/logging.js';
import { checkPasswordChangeToken } from '@adornis/users/db/a-user.js';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resetPasswordWithTokenMutation } from '../../../../db/api/password-reset.js';
import { router } from '../../../App.js';
import { BlurredBackdrop } from '../../../components/ui/modal-backdrop.js';
import Home from '../../page.js';
import { Button } from '../../../components/ui/button.js';
import LabeledInput, { validateInput } from '../../../components/ui/labeled-inputs.js';
import { classifyAuthError } from '../../../lib/auth-errors.js';

const PASSWORD_INVALID_FALLBACK = 'Das Passwort erfüllt die Anforderungen nicht.';
const INVALID_LINK_MESSAGE = 'Dieser Link ist ungültig oder abgelaufen.';

// The library answers `false` for a used or revoked link, but THROWS `InvalidTokenError` ("Invalid token") for an
// expired one. For the user both mean the same, so both get INVALID_LINK_MESSAGE.
const isInvalidTokenError = (err: unknown) =>
  /invalid token/i.test(String((err as { message?: unknown })?.message ?? err));

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const token = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('token') ?? '';
  }, []);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const runTokenCheck = async () => {
      if (!token) {
        setTokenValid(false);
        setError('Der Link ist ungueltig oder unvollstaendig.');
        setIsCheckingToken(false);
        return;
      }

      try {
        const isValid = await checkPasswordChangeToken(token)();
        setTokenValid(isValid);
        if (!isValid) {
          setError(INVALID_LINK_MESSAGE);
        }
      } catch (err) {
        setTokenValid(false);
        if (isInvalidTokenError(err)) {
          setError(INVALID_LINK_MESSAGE);
        } else {
          logger.error(err, `Failed to validate password reset token: ${token}`);
          setError('Der Link konnte nicht geprüft werden. Bitte fordere einen neuen Link an.');
        }
      } finally {
        setIsCheckingToken(false);
      }
    };

    void runTokenCheck();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!tokenValid || !token) {
      setError('Der Reset-Link ist nicht gültig.');
      return;
    }

    if (!validateInput(password, 'password')) {
      setError(t('AuthModal.passwordInvalid', { defaultValue: PASSWORD_INVALID_FALLBACK }));
      return;
    }

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPasswordWithTokenMutation(token, password, true)();
      setSuccess(true);
    } catch (err) {
      logger.error(err, `Password reset failed for token: ${token}`);
      // The server checks the password before it uses up the link, so for a rejected password the link still works.
      setError(
        classifyAuthError(err) === 'password'
          ? t('AuthModal.passwordInvalid', { defaultValue: PASSWORD_INVALID_FALLBACK })
          : 'Das Passwort konnte nicht geändert werden. Bitte fordere einen neuen Link an.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BlurredBackdrop background={<Home />}>
      <div className="w-full max-w-md bg-white dark:bg-card rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-300">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">Neues Passwort setzen</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Vergib ein neues Passwort für dein Konto.</p>

        {isCheckingToken ? (
          <p className="text-sm text-slate-600 dark:text-slate-400">Link wird geprueft...</p>
        ) : success ? (
          <div className="space-y-4">
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
              Dein Passwort wurde erfolgreich geändert.
            </p>
            <Button onClick={() => router.goTo('home')} className="w-full bg-brand-500 hover:bg-[brand-600] text-white">
              Zurück zum Login
            </Button>
          </div>
        ) : !tokenValid ? (
          <div className="space-y-4">
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button onClick={() => router.goTo('home')} className="w-full bg-brand-500 hover:bg-[brand-600] text-white">
              Zurück
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <LabeledInput
              id="new-password"
              name="newPassword"
              label="Neues Passwort"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              hint={t('AuthModal.passwordRequirements', {
                defaultValue:
                  'Mindestens 8 Zeichen, mit Groß- und Kleinbuchstaben, einer Ziffer und einem Sonderzeichen.',
              })}
            />
            <LabeledInput
              id="confirm-password"
              name="confirmPassword"
              label="Passwort bestaetigen"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />

            {error && <p className="text-sm text-red-600">{error}</p>}

            <Button
              type="submit"
              disabled={isSubmitting || !password || !confirmPassword}
              className="w-full bg-brand-500 hover:bg-[brand-600] text-white"
            >
              {isSubmitting ? 'Speichern...' : 'Passwort aktualisieren'}
            </Button>
          </form>
        )}
      </div>
    </BlurredBackdrop>
  );
}
